'use client'
import { memo, useEffect, useMemo, useState } from 'react'
import clsx from 'clsx'

import { ThemeMenu, ThemeMenuSubItem } from '@typings'
import { Loop } from '@utilities/helpers/core.helpers'

import { RaNavbarDropdownSection } from '../RaNavbarDropdownSection/RaNavbarDropdownSection'
import { RaNavbarDropdownSectionChild } from '../RaNavbarDropdownSectionChild/RaNavbarDropdownSectionChild'

import styles from './ra-navbar-dropdown.module.scss'

interface RaNavbarDropdownProps {
  menu: ThemeMenu
  initialActive?: string
  removeTopPadding?: boolean
  disablePrefetch?: boolean
  isOpen?: boolean
  flush?: boolean
}

type CurrentMenuProps = {
  index: number
  menu: ThemeMenuSubItem
}

export const RaNavbarDropdown = memo(
  ({
    menu,
    disablePrefetch,
    isOpen,
    flush,
    initialActive,
  }: RaNavbarDropdownProps) => {
    const initialActiveMenu = menu.subitems.find(
      item => item.link?.title?.toLowerCase() === initialActive
    )
    const [renderedMenu, setRenderedMenu] = useState<CurrentMenuProps | null>({
      index: 0,
      menu: initialActiveMenu || menu.subitems?.find(item => item.link),
    })

    const returnFocusToParent = () => {
      const activeEl = document.getElementById(
        `dropdownsection-${menu.link.title}-${renderedMenu.index}`
      )
      if (activeEl) {
        activeEl.focus()
      }
    }

    const renderParentSections = useMemo(() => {
      if (!menu?.subitems) return []

      const menuItems = menu.subitems.filter(item => item.link)

      return (
        <div>
          {Loop(menuItems, (item, i) => {
            return (
              <RaNavbarDropdownSection
                key={i}
                menu={item}
                setRenderedMenu={setRenderedMenu}
                currentMenu={renderedMenu?.menu?.link?.title}
                index={i}
                count={menu.subitems?.length}
                parent={menu.link.title}
              />
            )
          })}
        </div>
      )
    }, [menu.subitems, renderedMenu])

    const maximumMenuItems = menu.subitems.reduce((acc, curr) => {
      const validSubitems = curr.subitems?.filter(item => item.link)
      if (validSubitems?.length > acc) acc = validSubitems.length
      return acc
    }, 0)

    const maximumMenuColumns = Math.ceil(maximumMenuItems / 8)

    useEffect(() => {
      if (!renderedMenu) {
        const firstEl = document.getElementById(
          `dropdownsection-${menu.link.title}-0`
        )
        if (firstEl) {
          firstEl.focus()
        }
      }
    }, [isOpen])

    return (
      <div
        data-name="dropdown-menu"
        className={styles['nav-dropdown']}>
        <div
          className={clsx(
            styles['nav-dropdown__backdrop'],
            flush && styles['nav-dropdown__backdrop--flush']
          )}>
          <nav
            className={styles['nav-dropdown__content']}
            style={{ minWidth: maximumMenuColumns * 350 }}>
            {renderParentSections}
            <RaNavbarDropdownSectionChild
              menu={renderedMenu?.menu}
              disablePrefetch={disablePrefetch}
              returnFocusToParent={returnFocusToParent}
            />
          </nav>
        </div>
      </div>
    )
  }
)

RaNavbarDropdown.displayName = 'RaNavbarDropdown'
